import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap/";
import "./Skills.css";
import { useInView } from "react-intersection-observer";

function Skills() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const { ref: skillsRef, inView: skillsVisible } = useInView({
    threshold: 0.15,
    triggerOnce: true,
  });

  const categories = [
    { id: "all", label: "All Skills" },
    { id: "ai", label: "AI & Machine Learning" },
    { id: "languages", label: "Programming Languages" },
    { id: "web", label: "Full Stack Development" },
    { id: "cloud", label: "Cloud & DevOps" },
    { id: "business", label: "Product & Analytics" },
  ];

  const skillData = [
    // AI & Machine Learning
    { name: "Azure OpenAI", category: "ai", tag: "AI", icon: "🤖" },
    { name: "Agentic AI Development", category: "ai", tag: "AI", icon: "🧠" },
    { name: "Retrieval-Augmented Generation (RAG)", category: "ai", tag: "LLMs", icon: "⚡" },
    { name: "PyTorch", category: "ai", tag: "Deep Learning", icon: "🔥" },
    { name: "TensorFlow", category: "ai", tag: "Deep Learning", icon: "⚙️" },

    // Languages
    { name: "Python", category: "languages", tag: "Primary", icon: "🐍" },
    { name: "TypeScript", category: "languages", tag: "Web", icon: "📘" },
    { name: "JavaScript", category: "languages", tag: "Web", icon: "📜" },
    { name: "C#", category: "languages", tag: ".NET", icon: "🔷" },
    { name: "Java", category: "languages", tag: "Enterprise", icon: "☕" },
    { name: "C / C++", category: "languages", tag: "Systems", icon: "⚡" },
    { name: "Shell Scripting", category: "languages", tag: "Automation", icon: "💻" },

    // Full Stack
    { name: "React.js", category: "web", tag: "Frontend", icon: "⚛️" },
    { name: "Next.js", category: "web", tag: "Full Stack", icon: "▲" },
    { name: "AngularJS", category: "web", tag: "Frontend", icon: "🅰️" },
    { name: "Node.js", category: "web", tag: "Backend", icon: "🟢" },
    { name: "FastAPI", category: "web", tag: "Backend", icon: "⚡" },
    { name: ".NET", category: "web", tag: "Backend", icon: "💠" },
    { name: "REST APIs", category: "web", tag: "Integration", icon: "🔗" },

    // Cloud & DevOps
    { name: "Azure", category: "cloud", tag: "Cloud", icon: "☁️" },
    { name: "AWS Certified Cloud Practitioner", category: "cloud", tag: "Certification", icon: "🏅" },
    { name: "Docker", category: "cloud", tag: "Containers", icon: "🐳" },
    { name: "Azure DevOps", category: "cloud", tag: "CI/CD", icon: "🔄" },
    { name: "Jenkins", category: "cloud", tag: "Automation", icon: "🏗️" },
    { name: "Ansible", category: "cloud", tag: "Infrastructure", icon: "🛠️" },
    { name: "PostgreSQL", category: "cloud", tag: "Database", icon: "🗄️" },
    { name: "MongoDB", category: "cloud", tag: "Database", icon: "🍃" },
    { name: "Redis", category: "cloud", tag: "Caching", icon: "⚡" },

    // Business & Analytics
    { name: "Power BI", category: "business", tag: "Analytics", icon: "📊" },
    { name: "SQL", category: "business", tag: "Data", icon: "🗃️" },
    { name: "Enterprise Design Thinking", category: "business", tag: "IBM Certified", icon: "💡" },
    { name: "Product Management", category: "business", tag: "Strategy", icon: "📈" },
  ];

  const filteredSkills = skillData.filter((skill) => {
    const matchesCategory = activeCategory === "all" || skill.category === activeCategory;
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      skill.tag.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Container fluid className="skillsSection py-5" id="skills" ref={skillsRef}>
      <Container className={`${skillsVisible ? "fade-in-up" : "opacity-0"}`}>
        <div className="section-header text-center">
          <h2 className="section-title">Technical Matrix &amp; Skills</h2>
          <div className="titleHr" />
          <p className="section-subtitle">
            A collection of the technologies, frameworks, and business skills I've developed through industry and academic experience.
          </p>
        </div>

        {/* Filter Controls & Search */}
        <div className="skillsControls mb-4">
          <div className="categoryTabs">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`tabBtn ${activeCategory === cat.id ? "activeTab" : ""}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="searchBoxWrapper mt-3 mt-md-0">
            <input
              type="text"
              className="skillSearchInput"
              placeholder="🔍 Search skills (e.g. PyTorch, Azure, React)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Skills Grid */}
        <Row className="g-3">
          {filteredSkills.map((skill, idx) => (
            <Col xs={12} sm={6} md={4} lg={3} key={idx}>
              <div className="glass-card skillCard p-3 h-100">
                <div className="skillIcon">{skill.icon}</div>
                <div className="skillContent">
                  <div className="skillName">{skill.name}</div>
                  <span className="skillTag">{skill.tag}</span>
                </div>
              </div>
            </Col>
          ))}
          {filteredSkills.length === 0 && (
            <Col xs={12} className="text-center py-4">
              <p className="text-muted fs-5">No matching skills found for "{searchTerm}".</p>
            </Col>
          )}
        </Row>
      </Container>
    </Container>
  );
}

export default Skills;
